import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Building2, Bus, UserPlus, UserMinus, ChevronDown, ChevronRight, Receipt, Image as ImageIcon, UserCog, FileText, Download, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AddWorkerSheet } from '@/components/construction/AddWorkerSheet';

type Project = {
  id: string;
  code: string;
  nameEn: string;
  clientName: string;
  poNumber: string | null;
  villaRate: number | string;
};

type Overview = {
  project: Project;
  contract: { totalVillas: number; totalContractValue: number; villasComplete: number; villasInProgress: number; overallPct: number; };
  finance: { revenueEarned: number; totalLaborCost: number; totalExpenses: number; totalFixedCosts: number; totalCost: number; netProfit: number; margin: number; };
  operations: { activeWorkers: number; daysLogged: number; totalHours: number; flaggedDays: number; };
};

type Villa = { id: string; villaName: string; model: string; blockNumber: number; rate: number | string; progressPct: number; revenue: number; };
type Worker = { id: string; code: string; fullName: string; skill: string; hourlyRate: number | string; iqamaNumber: string | null; phone: string | null; isActive: boolean; };
type AttDay = { id: string; attendanceDate: string; busInAt: string | null; busOutAt: string | null; hours: number; photographed: number; totalLaborCost: number; };

const fmtSAR = (n: number) => `SAR ${Math.round(n).toLocaleString('en-US')}`;

export default function ConstructionPage() {
  const { user } = (window as any).__auth ?? {};
  const allowedRoles = ['super_admin', 'admin', 'construction_supervisor', 'auditor', 'accountant'];
  const [project, setProject] = useState<Project | null>(null);
  const [tab, setTab] = useState<'overview' | 'villas' | 'workers' | 'attendance' | 'ipc'>('overview');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get<Project>('/api/v1/construction/default')
      .then((res) => { setProject(res.data); setLoading(false); })
      .catch((e) => { setError(e?.response?.data?.message ?? e?.message ?? 'Failed'); setLoading(false); });
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen bg-background p-6">
      <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2 mb-4"><ArrowLeft className="h-4 w-4" />Back</Button></Link>
      <div className="text-rose-600">Error: {error}</div>
    </div>
  );
  if (!project) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" />Dashboard</Button></Link>
          <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-muted-foreground" /><span className="font-medium">Construction</span></div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-6">
        <div className="mb-6">
          <div className="flex items-start justify-between gap-3 mb-2">
          <h1 className="text-2xl font-medium">{project.nameEn}</h1>
          <div className="flex items-center gap-2">
            <ConstructionAdminLink projectId={project.id} />
            <Link
              to={`/dashboard/construction/projects/${project.id}/performance`}
              data-pr22="photos-button"
            >
              <Button size="sm" variant="outline" className="gap-2">
                <ImageIcon className="h-4 w-4" />
                Photos
              </Button>
            </Link>
            <Link
              to={`/dashboard/construction/projects/${project.id}/expenses`}
              data-pr21="expenses-button"
            >
              <Button size="sm" variant="outline" className="gap-2">
                <Receipt className="h-4 w-4" />
                Expenses
              </Button>
            </Link>
            <Link
              to={`/dashboard/construction/projects/${project.id}/attendance`}
              data-pr18="attendance-button"
            >
              <Button size="sm" className="gap-2">
                <Bus className="h-4 w-4" />
                Today's attendance
              </Button>
            </Link>
          </div>
        </div>
          <p className="text-sm text-muted-foreground mt-1">{project.clientName} · PO {project.poNumber ?? '—'} · SAR {Number(project.villaRate).toLocaleString()} per villa</p>
        </div>
        <nav className="flex gap-2 border-b border-border mb-6">
          {(['overview', 'villas', 'workers', 'attendance', 'ipc'] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm capitalize border-b-2 transition-colors ${tab === t ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              {t}
            </button>
          ))}
        </nav>
        {tab === 'overview' && <OverviewTab projectId={project.id} />}
        {tab === 'villas' && <VillasTab projectId={project.id} />}
        {tab === 'workers' && <WorkersTab projectId={project.id} />}
        {tab === 'attendance' && <AttendanceTab projectId={project.id} />}
        {tab === 'ipc' && <IpcTab projectId={project.id} />}
      </main>
    </div>
  );
}

function OverviewTab({ projectId }: { projectId: string }) {
  const [data, setData] = useState<Overview | null>(null);
  useEffect(() => { apiClient.get<Overview>(`/api/v1/construction/projects/${projectId}/overview`).then((r) => setData(r.data)); }, [projectId]);
  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  const stat = (label: string, value: string, tone?: 'good' | 'bad') => (
    <div className="bg-muted rounded-lg p-4">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className={`text-2xl font-medium ${tone === 'good' ? 'text-emerald-600' : tone === 'bad' ? 'text-rose-600' : ''}`}>{value}</div>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stat('Revenue earned', fmtSAR(data.finance.revenueEarned))}
        {stat('Total cost', fmtSAR(data.finance.totalCost))}
        {stat('Net profit', fmtSAR(data.finance.netProfit), data.finance.netProfit >= 0 ? 'good' : 'bad')}
        {stat('Margin', `${data.finance.margin.toFixed(0)}%`, data.finance.margin >= 20 ? 'good' : data.finance.margin >= 0 ? undefined : 'bad')}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {stat('Labor cost', fmtSAR(data.finance.totalLaborCost))}
        <Link
          to={`/dashboard/construction/projects/${data.project.id}/expenses`}
          className="bg-muted hover:bg-muted/70 transition-colors rounded-lg p-4 cursor-pointer"
        >
          <div className="text-xs text-muted-foreground mb-1">Site expenses</div>
          <div className="text-2xl font-medium">{fmtSAR(data.finance.totalExpenses)}</div>
        </Link>
        <FixedCostsStatCard projectId={data.project.id} amount={data.finance.totalFixedCosts} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stat('Villas complete', `${data.contract.villasComplete} / ${data.contract.totalVillas}`)}
        {stat('In progress', String(data.contract.villasInProgress))}
        {stat('Overall %', `${data.contract.overallPct.toFixed(1)}%`)}
        {stat('Active workers', String(data.operations.activeWorkers))}
      </div>
      {data.operations.flaggedDays > 0 && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 rounded-lg p-3 text-sm">
          ⚠ {data.operations.flaggedDays} day{data.operations.flaggedDays > 1 ? 's' : ''} with missing worker photos.
        </div>
      )}
    </div>
  );
}

function VillasTab({ projectId }: { projectId: string }) {
  const [villas, setVillas] = useState<Villa[]>([]);
  const [block, setBlock] = useState<string>('all');
  const [status, setStatus] = useState<string>('all');
  useEffect(() => {
    const params = new URLSearchParams();
    if (block !== 'all') params.set('block', block);
    if (status !== 'all') params.set('status', status);
    apiClient.get<Villa[]>(`/api/v1/construction/projects/${projectId}/villas${params.toString() ? `?${params}` : ''}`).then((r) => setVillas(r.data));
  }, [projectId, block, status]);
  const blocks = Array.from(new Set(villas.map((v) => v.blockNumber).filter(Boolean))).sort();
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4 items-center text-sm">
        <span className="text-muted-foreground">Status:</span>
        {(['all', 'todo', 'progress', 'done'] as const).map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1 rounded-full text-xs ${status === s ? 'bg-foreground text-background' : 'bg-muted hover:bg-muted/80'}`}>{s}</button>
        ))}
        <span className="text-muted-foreground ms-3">Block:</span>
        <button onClick={() => setBlock('all')} className={`px-3 py-1 rounded-full text-xs ${block === 'all' ? 'bg-foreground text-background' : 'bg-muted hover:bg-muted/80'}`}>All</button>
        {blocks.map((b) => (
          <button key={b} onClick={() => setBlock(String(b))} className={`px-3 py-1 rounded-full text-xs ${block === String(b) ? 'bg-foreground text-background' : 'bg-muted hover:bg-muted/80'}`}>B{b}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {villas.map((v) => (
          <Link
            key={v.id}
            to={`/dashboard/construction/projects/${projectId}/villas/${v.id}`}
            className="border border-border rounded-lg p-3 hover:border-foreground hover:bg-muted/30 transition-colors"
          >
            <div className="flex justify-between items-center text-sm font-medium"><span>{v.villaName}</span><span className="text-xs text-muted-foreground">B{v.blockNumber}</span></div>
            <div className="text-xs text-muted-foreground">{v.model}</div>
            <div className="h-1.5 bg-muted rounded mt-2 overflow-hidden">
              <div className={`h-full ${v.progressPct >= 100 ? 'bg-emerald-600' : v.progressPct > 0 ? 'bg-amber-500' : 'bg-muted-foreground/30'}`} style={{ width: `${v.progressPct}%` }} />
            </div>
            <div className="text-xs text-muted-foreground mt-1">{v.progressPct}% · {fmtSAR(v.revenue)}</div>
          </Link>
        ))}
      </div>
      {villas.length === 0 && <div className="text-center py-8 text-muted-foreground">No villas match.</div>}
    </div>
  );
}

function WorkersTab({ projectId }: { projectId: string }) {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const refresh = () => {
    apiClient
      .get<Worker[]>(`/api/v1/construction/projects/${projectId}/workers?includeInactive=true`)
      .then((r) => setWorkers(r.data));
  };
  useEffect(() => { refresh(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = workers.filter((w) => w.isActive);
  const inactive = workers.filter((w) => !w.isActive);

  async function markAsLeft(w: Worker) {
    if (!window.confirm(`Mark ${w.fullName} as left?

They will be moved to the inactive list. Past attendance is kept.`)) return;
    setRemovingId(w.id);
    try {
      await apiClient.patch(`/api/v1/construction/projects/${projectId}/workers/${w.id}`, {
        isActive: false,
        leftDate: new Date().toISOString().slice(0, 10),
      });
      refresh();
    } catch (err) {
      window.alert(`Could not update worker: ${extractErrorMessage(err)}`);
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{active.length} active workers</div>
        <Button size="sm" className="gap-2" onClick={() => setAddOpen(true)}>
          <UserPlus className="h-4 w-4" /> Add worker
        </Button>
      </div>

      <div className="space-y-2">
        {active.map((w) => (
          <div key={w.id} className="border border-border rounded-lg p-3 flex justify-between items-center text-sm gap-3">
            <div className="min-w-0">
              <div className="font-medium truncate">{w.fullName}</div>
              <div className="text-xs text-muted-foreground truncate">{w.code} · {w.skill} · {Number(w.hourlyRate)} SAR/hr{w.iqamaNumber ? ` · ${w.iqamaNumber}` : ''}</div>
            </div>
            <button
              type="button"
              aria-label={`Mark ${w.fullName} as left`}
              disabled={removingId === w.id}
              onClick={() => markAsLeft(w)}
              className="h-8 px-2 inline-flex items-center gap-1 rounded-md text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-30"
              title="Mark as left"
            >
              {removingId === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserMinus className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">Mark as left</span>
            </button>
          </div>
        ))}
        {active.length === 0 && (
          <div className="text-center py-6 text-muted-foreground text-sm">No active workers. Tap Add worker to start.</div>
        )}
      </div>

      {inactive.length > 0 && (
        <div className="pt-2">
          <button
            type="button"
            onClick={() => setShowInactive((s) => !s)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground py-1"
          >
            {showInactive ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Inactive ({inactive.length})
          </button>
          {showInactive && (
            <div className="space-y-2 mt-2">
              {inactive.map((w) => (
                <div key={w.id} className="border border-dashed border-border rounded-lg p-3 text-sm opacity-60">
                  <div className="font-medium">{w.fullName}</div>
                  <div className="text-xs text-muted-foreground">{w.code} · {w.skill} · {Number(w.hourlyRate)} SAR/hr{w.iqamaNumber ? ` · ${w.iqamaNumber}` : ''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AddWorkerSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={refresh}
        projectId={projectId}
      />
    </div>
  );
}

function AttendanceTab({ projectId }: { projectId: string }) {
  const [days, setDays] = useState<AttDay[]>([]);
  useEffect(() => { apiClient.get<AttDay[]>(`/api/v1/construction/projects/${projectId}/attendance`).then((r) => setDays(r.data)); }, [projectId]);
  if (days.length === 0) return <div className="text-center py-12 text-muted-foreground text-sm">No attendance records yet. Site supervisor enters daily attendance from the mobile app.</div>;
  return (
    <div className="space-y-2">
      {days.map((d) => (
        <div key={d.id} className="border border-border rounded-lg p-3 text-sm">
          <div className="flex justify-between"><div className="font-medium">{new Date(d.attendanceDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</div><div className="font-medium">{fmtSAR(d.totalLaborCost)}</div></div>
          <div className="text-xs text-muted-foreground mt-1">
            {d.busInAt ? `In ${new Date(d.busInAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'No arrival'}
            {' · '}
            {d.busOutAt ? `Out ${new Date(d.busOutAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'No departure'}
            {' · '}{d.hours.toFixed(1)} hrs · {d.photographed} photographed
          </div>
        </div>
      ))}
    </div>
  );
}

function ConstructionAdminLink({ projectId }: { projectId: string }) {
  const { user } = useAuth();
  const role = user?.role ?? '';
  if (role !== 'super_admin' && role !== 'admin') return null;
  return (
    <>
      <Link to={`/dashboard/construction/projects/${projectId}/fixed-costs`} data-pr25="fixed-costs-button">
        <Button size="sm" variant="outline" className="gap-2">
          <Banknote className="h-4 w-4" />
          Fixed costs
        </Button>
      </Link>
      <Link to="/dashboard/construction/supervisors" data-pr23="supervisors-button">
        <Button size="sm" variant="outline" className="gap-2">
          <UserCog className="h-4 w-4" />
          Supervisors
        </Button>
      </Link>
    </>
  );
}

interface IpcBill {
  project: { code: string; nameEn: string; clientName: string; poNumber: string | null };
  breakdown: { previousAmount: number; presentAmount: number; subTotal: number; vat: number; grandTotal: number; retention: number; netPayable: number };
  perVilla: { id: string; villaName: string; model: string; blockNumber: number | null; progressPct: number; revenue: number }[];
}

function IpcTab({ projectId }: { projectId: string }) {
  const [previousPct, setPreviousPct] = useState('0');
  const [bill, setBill] = useState<IpcBill | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const pct = Number(previousPct) || 0;
    apiClient
      .get<IpcBill>(`/api/v1/construction/projects/${projectId}/ipc?previousPct=${pct}`)
      .then((r) => { setBill(r.data); setError(null); })
      .catch((e) => setError(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [projectId, previousPct]);

  async function downloadPdf() {
    setDownloading(true);
    try {
      const pct = Number(previousPct) || 0;
      const res = await apiClient.get(`/api/v1/construction/projects/${projectId}/ipc/pdf?previousPct=${pct}`, { responseType: 'blob' });
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : 'IPC.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      window.alert(`Could not generate PDF: ${extractErrorMessage(e)}`);
    } finally {
      setDownloading(false);
    }
  }

  if (loading && !bill) return <div className="text-muted-foreground text-sm">Loading…</div>;
  if (error && !bill) return <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm p-3">{error}</div>;
  if (!bill) return null;

  const row = (label: string, value: string, bold = false, neg = false) => (
    <div className={`flex items-baseline justify-between py-1.5 ${bold ? 'font-medium' : ''}`}>
      <span className="text-sm">{label}</span>
      <span className={`font-mono text-sm ${neg ? 'text-rose-600 dark:text-rose-400' : ''}`}>{value}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <label htmlFor="prev-pct" className="block text-xs uppercase tracking-wider text-muted-foreground">Previously certified %</label>
          <Input
            id="prev-pct"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            max="100"
            value={previousPct}
            onChange={(e) => setPreviousPct(e.target.value)}
            className="w-32"
          />
        </div>
        <Button onClick={downloadPdf} disabled={downloading} className="gap-2">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download IPC PDF
        </Button>
      </div>

      <div className="border border-border rounded-lg p-4 space-y-1">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Bill summary</div>
        {row('Previously certified', fmtSAR(bill.breakdown.previousAmount))}
        {row('Present period', fmtSAR(bill.breakdown.presentAmount))}
        <div className="border-t border-border my-1"></div>
        {row('Sub-total', fmtSAR(bill.breakdown.subTotal), true)}
        {row('VAT (15%)', fmtSAR(bill.breakdown.vat))}
        {row('Grand total', fmtSAR(bill.breakdown.grandTotal), true)}
        {row('Retention (10%)', '(' + fmtSAR(bill.breakdown.retention) + ')', false, true)}
        <div className="border-t-2 border-foreground/40 my-1"></div>
        {row('Net payable', fmtSAR(bill.breakdown.netPayable), true)}
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">Per-villa breakdown ({bill.perVilla.length})</div>
        {bill.perVilla.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">No villas with progress yet — mark stages first.</div>
        ) : (
          <div className="divide-y divide-border">
            {bill.perVilla.map((v) => (
              <div key={v.id} className="px-4 py-2 flex items-center justify-between text-sm gap-3">
                <div className="flex-1 min-w-0 truncate">
                  <span className="font-medium">{v.villaName}</span>
                  <span className="text-muted-foreground text-xs ml-2">{v.model}{v.blockNumber != null ? ` · B${v.blockNumber}` : ''}</span>
                </div>
                <div className="text-xs text-muted-foreground tabular-nums w-12 text-right">{v.progressPct.toFixed(1)}%</div>
                <div className="font-mono text-sm w-28 text-right">{fmtSAR(v.revenue)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FixedCostsStatCard({ projectId, amount }: { projectId: string; amount: number }) {
  const { user } = useAuth();
  const role = user?.role ?? '';
  const isAdmin = role === 'super_admin' || role === 'admin';
  const inner = (
    <>
      <div className="text-xs text-muted-foreground mb-1">Fixed costs</div>
      <div className="text-2xl font-medium">{fmtSAR(amount)}</div>
    </>
  );
  if (!isAdmin) return <div className="bg-muted rounded-lg p-4">{inner}</div>;
  return (
    <Link to={`/dashboard/construction/projects/${projectId}/fixed-costs`} className="bg-muted hover:bg-muted/70 transition-colors rounded-lg p-4 cursor-pointer">
      {inner}
    </Link>
  );
}
